import { FC, HTMLAttributes, useEffect, useMemo } from "react";
import clsx from "classnames";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import {
  Query,
  pageGQL,
  User,
  featuredCardsGql,
  getAAASubscriptionGQL,
} from "@onmo/graphql/graphql";
import { useStyles } from "./ConsoleMobileContent.style";
import { useAppSelector } from "../../hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import { ConsoleActivityCard } from "../ConsoleActivityCard/ConsoleActivityCard";
import { ConsoleRecentPlayCard } from "../ConsoleRecentPlayCard/ConsoleRecentPlayCard";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { GameCategory } from "../GameCategory";
import { ConsoleMobileCards } from "../ConsoleMobileCards";
import {
  ConsoleByogMobileGroup,
  ConsoleMobileId,
  FeatureMobileGroup,
} from "../../../constants/console";
import {
  getFeatureCardsMap,
  getSubscriptionFeaturedCardId,
} from "../../../models/console";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { onMoveHomeCards } from "../../../models/gamepad";

interface IConsoleMobileContentProps {
  user?: User;
  totalTimePlayed: number;
  totalPlayedGames: number;
}

const ConsoleMobileContent: FC<
  HTMLAttributes<HTMLDivElement> & IConsoleMobileContentProps
> = (props) => {
  const classes = useStyles();
  const { user, totalTimePlayed, totalPlayedGames, className, ...otherProps } =
    props;
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { setCallback, cleanup } = useGamepadContext();
  const { data: essentialData } = useQuery<Query>(pageGQL, {
    variables: { input: { page: "essential" } },
  });
  const { data: featuredCardsData } = useQuery<Query>(featuredCardsGql, {
    variables: { input: { group: FeatureMobileGroup } },
  });
  const { data: byogFeaturedData } = useQuery<Query>(featuredCardsGql, {
    variables: { input: { group: ConsoleByogMobileGroup } },
  });
  const { data: dataSubscription } = useQuery<Query>(getAAASubscriptionGQL);

  const featureCards = useMemo(() => {
    const subscriptionFeaturedCardId = getSubscriptionFeaturedCardId(
      dataSubscription?.me?.subscription,
    );
    return getFeatureCardsMap(
      featuredCardsData,
      subscriptionFeaturedCardId.concat(ConsoleMobileId),
    );
  }, [dataSubscription?.me?.subscription, featuredCardsData]);

  const byogSection = useMemo(() => {
    const items = byogFeaturedData?.featuredCards?.items;
    if (!items?.length) return undefined;
    return {
      group: ConsoleByogMobileGroup,
      cards: items,
    } as Query["page"]["items"][number];
  }, [byogFeaturedData]);

  useEffect(() => {
    setCallback({
      onMoveAction: onMoveHomeCards,
    });

    return () => {
      cleanup();
    };
  }, [setCallback, cleanup]);

  return (
    <div {...otherProps} className={clsx(classes.root, clsx)}>
      <Typography
        variant={TypographyVariant.HeadingH5Bold}
        className={classes.username}
      >
        {user ? `Hello ${user.username}` : "Welcome to Smart Console"}
      </Typography>
      {isAuthenticated && (
        <ConsoleActivityCard
          totalTimePlayed={totalTimePlayed}
          totalPlayedGames={totalPlayedGames}
        />
      )}
      {isAuthenticated && <ConsoleRecentPlayCard />}
      <ConsoleMobileCards featureCards={featureCards} />
      <GameCategory
        section={essentialData?.page?.items?.[0]}
        defaultCardWidth={160}
        sessionTitle="Essential Pass Games"
        isHideRating
      />
      <GameCategory
        section={byogSection}
        defaultCardWidth={160}
        sessionTitle="Install & Play Games"
        isHideRating
      />
    </div>
  );
};

export default ConsoleMobileContent;
